import React, { useState } from 'react';
import type { Institution, Location } from '../api';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { GameDialog } from './GameDialog';
import './InstitutionDisplay.css';
import { AgentBadge } from './AgentBadge';
import { ExpeditionBadge } from './ExpeditionBadge';
import { Card } from './Card';

export const getInstitutionArtworkPath = (name: string, preview = false) =>
  `/Institutions/rework/${preview ? 'small/' : ''}${encodeURIComponent(name.replace(/\s+/g, ''))}${preview ? '.jpg' : '.png'}`;

const InstitutionDigitalCard: React.FC<{ institution: Institution }> = ({ institution }) => {
  const [open, setOpen] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [artworkFailed, setArtworkFailed] = useState(false);
  return <article className="institution-digital" aria-label={`${institution.name} Institution`}>
    <header><span>{institution.subtype}</span><h3>{institution.name}</h3></header>
    <div className="institution-overview">
      <div className="institution-portrait">
        {previewFailed ? <p role="status">Artwork preview unavailable.</p> : <img
          src={getInstitutionArtworkPath(institution.name, true)} alt={`${institution.name} artwork`}
          loading="lazy" decoding="async" width={200} height={200} onError={() => setPreviewFailed(true)} />}
        <button type="button" className="institution-artwork-open"
          aria-label={`View full artwork for ${institution.name}`} aria-haspopup="dialog"
          onClick={event => { event.stopPropagation(); setArtworkFailed(false); setOpen(true); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="10" cy="10" r="6" /><path d="m14.5 14.5 6 6" />
          </svg>
        </button>
      </div>
      <section aria-label="Setup"><h4>Setup</h4><p>{institution.setupDescription}</p></section>
    </div>
    {institution?.rules?.map(rule => <section key={rule.name} aria-label={rule.name}>
      <h4>{rule.name}</h4><p>{rule.description}</p>
    </section>)}
    {open && <GameDialog title={`${institution.name} artwork`} variant="artwork" onClose={() => setOpen(false)}>
      {artworkFailed ? <p role="alert">Artwork could not be loaded.</p> : <img className="institution-artwork-full"
        src={getInstitutionArtworkPath(institution.name)} alt={`${institution.name} full artwork`}
        decoding="async" width={1254} height={1254} onError={() => setArtworkFailed(true)} />}
    </GameDialog>}
  </article>;
};

export const InstitutionDisplay: React.FC<{ institution: Institution; locations?: Location[]; referenceOnly?: boolean }> = ({ institution, locations = [], referenceOnly = false }) => {
  const artwork = useMonsterTokenStyle();
  return <div className="institution-display">
    {artwork === 'Digital' ? <InstitutionDigitalCard key={institution.id} institution={institution} />
      : <div className="institution-classic"><img loading="lazy"
        src={`/Guardians/${encodeURIComponent(institution.name)}.jpg`} alt={`Institution ${institution.name}`} /></div>}
    {!referenceOnly && institution.id === 'institution_miskatonic_university' && <section className="institution-bureau-state" aria-label="University Expeditions">
      <h4>Expeditions: {locations.filter(location => location.hasExpedition).length} / 3</h4>
      {locations.some(location => location.hasExpedition) ? locations.filter(location => location.hasExpedition).map(location =>
        <p key={location.name}><ExpeditionBadge /> {location.city}: {location.name}</p>) : <p>No Expeditions launched.</p>}
    </section>}
    {!referenceOnly && institution.bureauState && <section className="institution-bureau-state" aria-label="Bureau resources">
      <h4>Agent pool: {institution.bureauState.availableAgents} / 38</h4>
      <AgentBadge count={institution.bureauState.availableAgents} />
      <h4>Protective Custody</h4>
      {!!institution.bureauState.custodyAllies && (
        <>
          {Object.entries(institution.bureauState.custodyAllies).length === 0 && <p>No Allies in custody.</p>}
          {Object.entries(institution.bureauState.custodyAllies).map(([city, ally]) => ally &&
            <section className="institution-custody-slot" key={city} aria-label={`${city} custody`}>
              <h5>{city}: {ally.name}</h5><Card card={{ kind: 'ally', item: ally }} />
            </section>)}
        </>
      )}
    </section>}
  </div>;
};
